<template>
  <div
    ref="editor"
    class="ai-logic-editor"
    contentEditable="true"
    spellcheck="false"
    autocorrect="off"
    autocomplete="off"
    autocapitalize="off"
    @input="onEditorInput"
    @keydown="onKeyDown"
    @keyup="onKeyUp"
    @mouseup="onMouseUp"
    @paste="onEditorPaste"
  >
    <!-- @mousedown="onMouseDown" -->
    <div
      v-for="node in editorNodes"
      :key="node.id"
      :data-block-id="node.id"
      class="editor-block"
      @click="showActionBtn"
    >
      <div
        v-if="node.view == getViewType.CODE"
        :id="node.id"
        class="node-block code"
        :data-view="getViewType.CODE"
      ></div>
      <div
        v-else="node.view == getViewType.NL"
        :data-view="getViewType.NL"
        :id="node.id"
        class="node-block nl"
      ></div>
    </div>
  </div>
</template>

<script>
import AILogicDropdown from './AILogicDropdown';
import {
  aiLogiceditorNodeViewType,
  aiLogicEditorNodeViewClass,
  aiLogicEditorViewStateKey,
} from '@/utils/constants/AILogicConstants';
import { generateUUID } from '@/utils/helpers';
import { askChatGPT } from '../../services/workflow.service';
export default {
  name: 'AILogicEditor',
  data() {
    return {
      selectedRange: null,
      initialNode: {
        id: generateUUID(),
        SNL: '',
        code: '',
        view: 'NL',
      },
    };
  },
  props: {
    editorNodes: {
      type: Array,
      default: () => [],
    },
    endPoint: {
      type: String,
    },
    action: {
      type: Array,
      default: [],
    },
  },

  methods: {
    setNodeValues(nodes) {
      this.$nextTick(() => {
        nodes.forEach((node) => {
          this.updateNodeText(node);
        });
      });
    },
    getSelectedNodeBlocks() {
      const selection = window.getSelection();
      if (!selection.rangeCount) return [];
      if (selection.focusNode?.classList?.contains('result-wrapper')) {
        const allBlocks = Array.from(
          document.querySelectorAll('.editor-block')
        );
        const selectedBlocks = [];

        let nodeforNL;
        allBlocks.forEach((node, index) => {
          if (
            (node.dataset.blockId === this.editorNodes[index].id &&
              !this.editorNodes[index].code) ||
            (node.dataset.blockId === this.editorNodes[index].id &&
              this.editorNodes[index].code)
          ) {
            nodeforNL = node;
          }
        });

        selectedBlocks.push(nodeforNL);

        return selectedBlocks;
      } else {
        const userRange = selection.getRangeAt(0);
        const allBlocks = Array.from(
          document.querySelectorAll('.editor-block')
        );
        const selectedBlocks = [];

        allBlocks.forEach((block) => {
          // If the userRange intersects the block in any way:
          if (userRange.intersectsNode(block)) {
            selectedBlocks.push(block);
          }
        });

        return selectedBlocks;
      }
    },
    getSelectedBlockIds() {
      const selectedBlocks = this.getSelectedNodeBlocks();
      const blockIds = selectedBlocks.map((block) =>
        block.getAttribute('data-block-id')
      );
      return blockIds;
    },
    mergeSelectedNodes() {
      // 1) Get all selected block elements
      const selectedBlocks = this.getSelectedNodeBlocks();
      if (selectedBlocks.length <= 1) {
        return;
      }

      // 2) Extract their IDs and find them in your editorNodes array
      const blockIds = selectedBlocks.map((block) =>
        block.getAttribute('data-block-id')
      );

      // Sort them by their index in editorNodes to preserve order
      const indexes = blockIds
        .map((id) => this.editorNodes.findIndex((n) => n.id == id))
        .sort((a, b) => a - b);

      // 3) Combine the text from each node, using \n or <br>
      //    Here, we use "\n" for data, and later replace with <br> on render
      const mergedText = indexes.map((i) => this.editorNodes[i].SNL).join('\n');

      // 4) Place merged text into the first selected node
      this.editorNodes[indexes[0]].SNL = mergedText;

      // 5) Remove the subsequent nodes from the array
      //    (in descending order so we don't mess up the indexes)
      for (let i = indexes.length - 1; i >= 1; i--) {
        this.editorNodes.splice(indexes[i], 1);
      }
    },
    getClosestNodeBlock() {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      if (this.selectedRange) {
        const selection = window.getSelection();
        selection.removeAllRanges(); // Clear existing selection
        selection.addRange(this.selectedRange); // Restore saved selection
      }
      if (selection.focusNode?.classList?.contains('result-wrapper')) {
        let nodes = document.querySelectorAll('.node-block');

        let nodeforNL;
        nodes.forEach((node, index) => {
          if (
            node.id === this.editorNodes[index].id &&
            !this.editorNodes[index].code
          ) {
            nodeforNL = node;
          }
        });
        const nodeView = nodeforNL.getAttribute('data-view');

        const editorCurrentBlock = nodeforNL.closest('.editor-block');
        return editorCurrentBlock.querySelector(
          `.node-block.${aiLogicEditorNodeViewClass[nodeView]}`
        );
      } else {
        let node = selection.anchorNode;

        if (node.nodeType === Node.TEXT_NODE) {
          node = node.parentElement;
        }

        const nodeView = node.getAttribute('data-view');

        const editorCurrentBlock = node.closest('.editor-block');
        return editorCurrentBlock.querySelector(
          `.node-block.${aiLogicEditorNodeViewClass[nodeView]}`
        );
      }
    },
    onEditorInput(event) {
      let sel = window.getSelection();
      if (sel.rangeCount === 0) return;
      let range = sel.getRangeAt(0);
      let rect = range.getBoundingClientRect();
      this.$emit('onMouseDown', rect);
      const element = this.getClosestNodeBlock();
      if (!element) return;
      const nodeIndex = this.editorNodes.findIndex(
        (node) => node.id == element.id
      );
      if (nodeIndex == -1) return;
      const node = this.editorNodes[nodeIndex];
      if (!node) return;
      if (event.inputType == 'deleteContentBackward') {
        if (nodeIndex < this.editorNodes.length - 1) {
          const nextNode = this.editorNodes[nodeIndex + 1];
          const nextNodeElementSNL = this.$refs.editor.querySelector(
            `[id="${nextNode.id}"].nl`
          );
          if (nextNodeElementSNL) {
            nextNode.SNL = nextNodeElementSNL.innerText;
          }
          const nextNodeElementCode = this.$refs.editor.querySelector(
            `[id="${nextNode.id}"].code`
          );
          if (nextNodeElementCode) {
            nextNode.code = nextNodeElementCode.innerText;
          }
          this.$forceUpdate();
        }
      }
      if (node.view == aiLogiceditorNodeViewType.CODE) {
        node.code = element.innerText == '\n' ? '' : element.innerText;
      } else {
        if (element.innerText == '\n') {
          element.innerText = '';
          node.SNL = '';
          node.code = '';
        } else {
          node.SNL = element.innerText;
        }
      }
    },

    onEditorPaste(event) {
      event.preventDefault();

      const clipboardData = event.clipboardData || window.clipboardData;
      const pastedText = clipboardData.getData('text/plain');
      if (!pastedText.trim()) return;

      let insertIndex = this.editorNodes.length;
      let closestBlock = null;
      let selectedText = '';

      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        let anchorNode = selection.anchorNode;
        closestBlock =
          anchorNode?.nodeType === Node.ELEMENT_NODE
            ? anchorNode.closest('.node-block')
            : anchorNode?.parentElement?.closest('.node-block');

        if (closestBlock) {
          insertIndex = this.editorNodes.findIndex(
            (n) => n.id == closestBlock.id
          );
          selectedText = selection.toString(); // Get selected text
        }
      }

      const currentNode = this.editorNodes[insertIndex];
      let pastedNodes = [];

      try {
        const parsedData = JSON.parse(pastedText);
        if (Array.isArray(parsedData)) {
          pastedNodes = parsedData.map((node) => ({
            id: node.id || generateUUID(),
            SNL: node.SNL || '',
            code: node.code || '',
            view: 'NL',
          }));
        }
      } catch (error) {
        if (currentNode.view == aiLogiceditorNodeViewType.CODE) {
          pastedNodes = [
            {
              id: generateUUID(),
              SNL: pastedText.trim(),
              code: pastedText.trim(),
              view: 'CODE',
            },
          ];
        } else {
          pastedNodes = pastedText.split('\n').map((text) => ({
            id: generateUUID(),
            SNL: text.trim(),
            code: '',
            view: 'NL',
          }));
        }
      }

      const { before: beforeText, after: afterText } =
        this.splitContentAtCursor(closestBlock);

      if (selectedText && closestBlock) {
        // If text is selected, replace the selected text with pasted content
        const fullText = beforeText + afterText;
        const updatedText = fullText.replace(selectedText, pastedNodes[0].SNL);

        currentNode[aiLogicEditorViewStateKey[currentNode.view]] = updatedText;
        this.updateNodeText(this.editorNodes[insertIndex]);

        setTimeout(() => {
          this.setCaretIndex(
            closestBlock,
            beforeText.length + pastedNodes[0].SNL.length
          );
        });

        return;
      }

      if (closestBlock && insertIndex !== -1) {
        const cursorPosition = this.getCaretIndex(closestBlock);
        const pasteableText =
          pastedNodes[0][aiLogicEditorViewStateKey[currentNode.view]];

        currentNode[aiLogicEditorViewStateKey[currentNode.view]] = beforeText;
        currentNode[aiLogicEditorViewStateKey[currentNode.view]] +=
          pasteableText;

        pastedNodes.shift();
        if (!pastedNodes.length) {
          currentNode[aiLogicEditorViewStateKey[currentNode.view]] += afterText;
          setTimeout(() => {
            this.updateNodeText(this.editorNodes[insertIndex]);
            this.setCaretIndex(
              closestBlock,
              cursorPosition + pasteableText.length
            );
          });

          return;
        }
      }

      if (pastedNodes.length > 0) {
        this.editorNodes.splice(insertIndex + 1, 0, ...pastedNodes);
        const lastPastedNode =
          this.editorNodes[insertIndex + pastedNodes.length];
        lastPastedNode.SNL += afterText;
      }

      setTimeout(() => {
        this.updateNodeText(this.editorNodes[insertIndex]);
        this.setNodeValues(pastedNodes);
        const lastPastedNode = pastedNodes.length
          ? pastedNodes[pastedNodes.length - 1]
          : this.editorNodes[insertIndex];

        const lastElement = this.$refs.editor.querySelector(
          `[id="${lastPastedNode.id}"].nl`
        );
        setTimeout(() => {
          this.setCaretIndex(lastElement, lastPastedNode.SNL.length);
        });
      });
    },

    onKeyUp(event) {
      if (event.key == 'a' && event.ctrlKey) {
        this.$emit('onTextSelect');
      }
    },
    async onKeyDown(event) {
      const element = this.getClosestNodeBlock();

      if (!element) return;

      const nodeIndex = this.editorNodes.findIndex(
        (node) => node.id == element.id
      );

      if (event.key === 'Backspace' && element.innerText.trim() === '') {
        if (this.editorNodes.length == 1) {
          this.editorNodes[0].code = '';
          event.preventDefault();
          return;
        }
      }

      if (event.key === 'Enter') {
        event.preventDefault();

        if (nodeIndex === -1) return;
        const currentNode = this.editorNodes[nodeIndex];
        const nodeView = currentNode.view;

        // handle code input inside single element
        // if node view is code then manage code inside single element if it is not code then create separate node

        if (nodeView == aiLogiceditorNodeViewType.CODE) {
          // -------------------------------------------------------------------------------------
          const selection = window.getSelection();
          if (!selection.rangeCount) return;

          const range = selection.getRangeAt(0);
          const br = document.createElement('br');

          const parentBlock =
            range.startContainer.nodeType === Node.ELEMENT_NODE
              ? range.startContainer
              : range.startContainer.parentElement;

          const isEmptyBlock = parentBlock.innerText.trim() === '';

          // Check if the cursor is at the absolute last position of the content
          const isAtEndOfContent =
            range.startContainer.nodeType === Node.TEXT_NODE &&
            range.startOffset === range.startContainer.textContent.length &&
            range.startContainer === parentBlock.lastChild; // Ensure it's the last child

          // If block is empty, insert only one `<br>`
          if (isEmptyBlock) {
            parentBlock.innerHTML = ''; // Clear any existing empty nodes
            parentBlock.appendChild(br);
          } else {
            range.insertNode(br);

            // If cursor is at the absolute last character, insert an additional <br>
            if (isAtEndOfContent) {
              const br2 = document.createElement('br');
              range.insertNode(br2);
              range.setStartAfter(br2);
              range.setEndAfter(br2);
            } else {
              range.setStartAfter(br);
              range.setEndAfter(br);
            }
          }

          // Move the selection cursor properly
          selection.removeAllRanges();
          selection.addRange(range);

          const nodeElement = this.getClosestNodeBlock();
          this.editorNodes[nodeIndex].code = nodeElement.innerText;

          // -------------------------------------------------------------------------------------
        } else {
          const nodes = this.editorNodes;
          const selectedNodes = this.getSelectedBlockIds();
          const indexes = selectedNodes
            .map((id) => nodes.findIndex((n) => n.id == id))
            .sort((a, b) => a - b);
          const mergedText = indexes.map((i) => nodes[i].SNL).join('\n');
          const promptMessage = `Your task is to take unstructured, informal, or grammatically incorrect user input and convert it into clear, structured, 
          and grammatically correct standard natural language. Follow these steps:
          1. Identify the core meaning and purpose of the user's message.
          2. Fix any grammatical errors, typos, or informal language.
          3. If the input is ambiguous or redundant, simplify and clarify it while keeping the original meaning intact.
          4. Ensure the sentence follows proper syntax and formal writing conventions.
          5. Do not change the original intent, tone, or message, unless the input is vague, in which case, provide a clearer version.
          6. Don't give code only convert UNL to Standard Natural Language.
          7. Give only Standard Natural Language. Not any other text.
          8. strickly follow above all instructions.
          UNL = ${mergedText}`;
          const formData = new FormData();
          formData.append(
            'props',
            JSON.stringify({ prompt: promptMessage, model: 'gpt-4' })
          );
          formData.append('usecase', 'ailogic_code');
          let sanitizedCode;
          if (!nodes[indexes].code && nodes[indexes].SNL) {
            this.action[0].isLoading = true;
            const res = await askChatGPT(this.endPoint, formData);
            this.$emit('onCodeGenerate', this.action[0]);
            sanitizedCode = res.data.message;
            nodes[indexes].SNL = sanitizedCode;
          } else {
            const newElementId = generateUUID();
            if (this.getSelectedBlockIds().length == 1) {
              const { before: beforeText, after: afterText } =
                this.splitContentAtCursor(this.getClosestNodeBlock());
              this.editorNodes[nodeIndex].SNL = beforeText;

              this.editorNodes.splice(nodeIndex + 1, 0, {
                ...this.initialNode,
                id: newElementId,
                SNL: afterText,
                code: !beforeText ? this.editorNodes[nodeIndex].code : '',
              });
              if (!beforeText) {
                this.editorNodes[nodeIndex].code = '';
              }
            }
            setTimeout(() => {
              this.updateNodeText(this.editorNodes[nodeIndex]);
              this.updateNodeText(this.editorNodes[nodeIndex + 1]);
              const newElement = document.querySelector(
                `[id="${newElementId}"].nl`
              );
              this.setCaretIndex(newElement, 0);
            });
          }
        }
      }
      if (event.key == 'z' && event.ctrlKey) {
        event.preventDefault(); // undo is disabled for now
      }
    },
    getCaretIndex(element) {
      const selection = window.getSelection();
      if (!selection.rangeCount) return 0;

      const range = selection.getRangeAt(0);
      let position = 0;

      function getNodeContribution(node) {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.nodeValue.length;
        } else if (node.nodeName === 'BR') {
          return 1;
        } else {
          return sumNodeContributions(node);
        }
      }

      function sumNodeContributions(node) {
        let sum = 0;
        for (let child of node.childNodes) {
          sum += getNodeContribution(child);
        }
        return sum;
      }

      function traverseNodes(node) {
        if (node === range.startContainer) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            for (let i = 0; i < range.startOffset; i++) {
              if (node.childNodes[i]) {
                position += getNodeContribution(node.childNodes[i]);
              }
            }
          } else if (node.nodeType === Node.TEXT_NODE) {
            position += range.startOffset;
          }
          return true;
        }

        if (node.nodeType === Node.TEXT_NODE) {
          position += node.nodeValue.length;
        } else if (node.nodeName === 'BR') {
          position += 1;
        }

        for (let child of node.childNodes) {
          if (traverseNodes(child)) return true;
        }

        return false;
      }

      if (element) {
        traverseNodes(element);
      }
      return position;
    },

    splitContentAtCursor(element) {
      let before = '';
      let after = '';
      let currentPos = 0;
      const cursorPos = this.getCaretIndex(element); // To store adjusted cursor position

      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_ALL, // Show all nodes (text & elements)
        null
      );

      let node;
      let splitHappened = false;

      while ((node = walker.nextNode())) {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          if (!splitHappened) {
            if (currentPos + text.length <= cursorPos) {
              before += text;
              currentPos += text.length;
            } else {
              const splitPos = cursorPos - currentPos;
              before += text.slice(0, splitPos);
              after += text.slice(splitPos);
              currentPos = cursorPos;
              splitHappened = true;
            }
          } else {
            after += text;
          }
        } else if (node.nodeName === 'BR') {
          if (!splitHappened) {
            if (currentPos < cursorPos) {
              before += '\n';
              currentPos += 1;
            } else {
              if (!splitHappened) {
                after += '\n';
              } else {
                currentPos += 1;
              }
              splitHappened = true;
            }
          } else {
            if (splitHappened) {
              after += '\n';
            }
          }
        } else if (
          node.nodeType === Node.ELEMENT_NODE &&
          node.childNodes.length === 0
        ) {
          // Empty nodes handling
          if (!splitHappened) {
            before += node.outerHTML;
            currentPos += 1; // Treat as one character
          } else {
            after += node.outerHTML;
          }
        }
      }

      return {
        before,
        after,
      };
    },

    // editor functions
    clearSelection() {
      const selection = window.getSelection();
      selection.removeAllRanges();
      this.selectedRange = null;
    },

    onMouseUp() {
      this.$emit('onTextSelect');
    },
    onMouseDown() {
      this.clearSelection();
    },

    updateNodeText(node) {
      const nlNodeElement = document.querySelector(`[id="${node.id}"].nl`);
      const codeNodeElement = document.querySelector(`[id="${node.id}"].code`);
      if (nlNodeElement) {
        nlNodeElement.innerText = node.SNL;
      }
      if (codeNodeElement) {
        codeNodeElement.innerText = node.code;
      }
    },

    //later move it to helper if needed at multiple places
    setCaretIndex(element, position) {
      const selection = window.getSelection();
      const range = document.createRange();

      // If the element is empty, create a text node and set caret inside it
      if (!element.hasChildNodes()) {
        const textNode = document.createTextNode('');
        element.appendChild(textNode);
        range.setStart(textNode, 0);
      } else {
        let charCount = 0;
        let nodeFound = false;

        function traverseNodes(node) {
          for (let child of node.childNodes) {
            if (child.nodeType === Node.TEXT_NODE) {
              let nextCharCount = charCount + child.length;
              if (!nodeFound && nextCharCount >= position) {
                range.setStart(child, position - charCount);
                nodeFound = true;
                break;
              }
              charCount = nextCharCount;
            } else {
              traverseNodes(child);
            }
          }
        }

        traverseNodes(element);
        if (!nodeFound) {
          range.setStart(element, element.childNodes.length);
        }
      }

      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    },
    handleRemovedNodes(removedNodes) {
      const filterdNodes = this.editorNodes.filter(
        (node) => !removedNodes.includes(node.id)
      );
      this.$emit('update:editorNodes', filterdNodes); // to keep parent state sync with props
    },
    initMutationObserver() {
      const observer = new MutationObserver((mutations) => {
        const removedNodes = mutations.reduce((acc, mutation) => {
          if (mutation.type === 'childList') {
            const nodes = Array.from(mutation.removedNodes)
              .map((removedNode) => removedNode.getAttribute('data-block-id'))
              .filter((id) => id); // Filter out invalid IDs

            return acc.concat(nodes);
          }
          return acc;
        }, []);

        if (removedNodes.length) {
          this.handleRemovedNodes(removedNodes);
        }
      });

      observer.observe(this.$refs.editor, { childList: true, subtree: false });
    },

    getCode() {
      return this.editorNodes.map((node) => node.code)?.join('\n');
    },
    showActionBtn() {
      let sel = window.getSelection();
      if (sel.rangeCount === 0) return;
      let range = sel.getRangeAt(0);
      let rect = range.getBoundingClientRect();
      this.$emit('onMouseDown', rect);
    },
  },
  mounted() {
    this.setNodeValues(this.editorNodes);

    if (!this.editorNodes.length) {
      this.editorNodes.push({ ...this.initialNode, id: generateUUID() });
    }
    this.initMutationObserver();
  },
  computed: {
    getViewType() {
      return aiLogiceditorNodeViewType;
    },
  },
  components: {
    AILogicDropdown,
  },
};
</script>

<style scoped>
.ai-logic-editor {
  height: 100%;
  outline: none;
  color: black;
  font-size: 16px;
  line-height: 1.5;
  padding: 16px;
  overflow: scroll;
}
.ai-logic-editor .editable-area {
  padding: 10px;
  min-height: 100px;
  white-space: pre-wrap;
}

.ai-logic-editor .node-block {
  min-height: 20px;
}

.ai-logic-editor .floating-dropdown {
  position: fixed;
  z-index: 10;
}
.ai-logic-editor .floating-dropdown.hide {
  display: none;
}

.ai-logic-editor .node-block.code {
  background: #f9f9f9;
  padding-inline: 2px;
  white-space: pre-wrap;
  font-family: monospace;
  background-color: #191919;
  color: white;
}
.ai-logic-editor [contenteditable='false'] {
  user-select: text;
  cursor: text;
}
</style>
